import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Edit2, Trash2, Send, ExternalLink, X, Save } from "lucide-react";

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  pending_review: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-100 text-rose-700 border-rose-200",
  inactive: "bg-slate-100 text-slate-500 border-slate-200",
};

const PRICE_UNITS = [
  { value: "per_order", label: "per order" },
  { value: "per_visit", label: "per visit" },
  { value: "per_month", label: "per month" },
  { value: "per_unit", label: "per unit" },
];

const EMPTY = {
  name: "",
  description: "",
  category: "",
  price: "",
  price_unit: "per_order",
  sla_days: "",
  custom_link: "",
  custom_link_reason: "",
  notes: "",
  submit_for_review: false,
};

export default function VendorServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | row
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const refresh = () =>
    api
      .get("/vendor/services")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (row) => {
    setForm({
      name: row.name || "",
      description: row.description || "",
      category: row.category || "",
      price: row.price ?? "",
      price_unit: row.price_unit || "per_order",
      sla_days: row.sla_days ?? "",
      custom_link: row.custom_link || "",
      custom_link_reason: row.custom_link_reason || "",
      notes: row.notes || "",
      submit_for_review: false,
    });
    setEditing(row);
  };

  const save = async (submitNow = false) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.price === "" ? null : Number(form.price),
        sla_days: form.sla_days === "" ? null : Number(form.sla_days),
        submit_for_review: submitNow,
      };
      if (editing === "new") {
        await api.post("/vendor/services", payload);
        toast.success(submitNow ? "Submitted for admin review" : "Saved as draft");
      } else {
        await api.patch(`/vendor/services/${editing.id}`, payload);
        toast.success(submitNow ? "Resubmitted for review" : "Saved");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/vendor/services/${row.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-services-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">
            Vendor · Services Engine
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">Your service catalog</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Design the services you offer to clinics — set price, SLA, optional external order link.
            Drafts are private; submit for admin review to make them visible to clinics.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="vendor-services-new-btn"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
        >
          <Plus className="w-4 h-4" /> New service
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center"
          data-testid="vendor-services-empty"
        >
          <div className="text-sm text-slate-600 mb-3">
            You haven't designed any services yet.
          </div>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            <Plus className="w-4 h-4" /> Create your first service
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vendor-services-list">
          {items.map((row) => (
            <article
              key={row.id}
              className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-sm transition"
              data-testid={`vendor-service-card-${row.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">
                    {row.category || "Uncategorized"}
                  </div>
                  <div className="font-semibold text-slate-900 mt-0.5">{row.name}</div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded-full border ${
                    STATUS_PILL[row.status] || STATUS_PILL.draft
                  }`}
                >
                  {row.status?.replace("_", " ")}
                </span>
              </div>
              {row.description && (
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{row.description}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
                {row.price != null && (
                  <span className="font-semibold text-slate-900">
                    ${Number(row.price).toFixed(2)}{" "}
                    <span className="text-slate-500 font-normal">
                      {(PRICE_UNITS.find((p) => p.value === row.price_unit) || {}).label || row.price_unit}
                    </span>
                  </span>
                )}
                {row.sla_days != null && <span>SLA: {row.sla_days}d</span>}
                {row.custom_link && (
                  <a
                    href={row.custom_link}
                    target="_blank"
                    rel="noreferrer"
                    title={row.custom_link_reason || "Exception: external order link"}
                    className="inline-flex items-center gap-1 text-amber-700 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" /> External link · exception
                  </a>
                )}
              </div>
              {row.admin_notes && (
                <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-900">
                  <span className="font-semibold">Admin notes:</span> {row.admin_notes}
                </div>
              )}
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => openEdit(row)}
                  data-testid={`vendor-service-edit-${row.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-1.5"
                >
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={() => remove(row)}
                  data-testid={`vendor-service-delete-${row.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg px-3 py-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="vendor-service-modal"
          >
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                {editing === "new" ? "Design a new service" : `Edit · ${editing.name}`}
              </h2>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Service name *">
                <input
                  data-testid="vendor-service-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="ipt"
                  placeholder="e.g. Mobile Phlebotomy"
                />
              </Field>
              <Field label="Description">
                <textarea
                  data-testid="vendor-service-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="ipt"
                  placeholder="What's included, who is it for, and how it works."
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Category">
                  <input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="ipt"
                    placeholder="e.g. diagnostics"
                  />
                </Field>
                <Field label="SLA (days)">
                  <input
                    data-testid="vendor-service-sla"
                    value={form.sla_days}
                    onChange={(e) => setForm({ ...form, sla_days: e.target.value })}
                    type="number"
                    min="0"
                    className="ipt"
                    placeholder="e.g. 3"
                  />
                </Field>
                <Field label="Price">
                  <input
                    data-testid="vendor-service-price"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    type="number"
                    min="0"
                    step="0.01"
                    className="ipt"
                    placeholder="e.g. 149.00"
                  />
                </Field>
                <Field label="Price unit">
                  <select
                    value={form.price_unit}
                    onChange={(e) => setForm({ ...form, price_unit: e.target.value })}
                    className="ipt"
                  >
                    {PRICE_UNITS.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field
                label="Custom external order link · exceptions only"
                hint={
                  <span>
                    Reserved for orders that genuinely cannot be fulfilled inside Nodera (e.g.
                    compounded medication orders requiring a state-specific pharmacy portal).
                    <strong className="text-slate-700"> If your service can be ordered through Nodera, leave this blank</strong> — clinics shouldn't have to leave the platform.
                  </span>
                }
              >
                <input
                  data-testid="vendor-service-custom-link"
                  value={form.custom_link}
                  onChange={(e) => setForm({ ...form, custom_link: e.target.value })}
                  className="ipt"
                  placeholder="https://"
                />
              </Field>
              {form.custom_link && (
                <Field
                  label="Why does this service require a custom link? *"
                  hint="Required when a custom link is set. Admin reviews this before approval."
                >
                  <textarea
                    data-testid="vendor-service-custom-link-reason"
                    value={form.custom_link_reason}
                    onChange={(e) => setForm({ ...form, custom_link_reason: e.target.value })}
                    rows={2}
                    className="ipt"
                    placeholder="e.g. Compounded GLP-1 dosing requires our 503A pharmacy's state-licensed ordering portal."
                  />
                </Field>
              )}
              <Field label="Internal notes (vendor-only, never shown to clinics)">
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="ipt"
                />
              </Field>
            </div>
            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-100 px-6 py-4 flex flex-wrap items-center justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="text-sm font-semibold text-slate-700 px-4 py-2 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => save(false)}
                disabled={saving || !form.name?.trim() || (form.custom_link && !form.custom_link_reason?.trim())}
                data-testid="vendor-service-save-draft"
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 bg-slate-200 hover:bg-slate-300 rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Save draft
              </button>
              <button
                onClick={() => save(true)}
                disabled={saving || !form.name?.trim() || (form.custom_link && !form.custom_link_reason?.trim())}
                data-testid="vendor-service-submit-review"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" /> Submit for review
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ipt {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 0.5rem 0.75rem;
          font-size: 14px;
          color: #0f172a;
          background: white;
        }
        .ipt:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 2px rgba(16,185,129,0.15); }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </label>
  );
}
